import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faHammer, faIndustry, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import {
    createDesignFromIntake,
    PRODUCT_DESCRIPTIONS,
    PRODUCT_STANDARD,
} from '../../../utils/designIntake';
import Modal from '../../helpers/Modal';
import moduleImage from '../../../assets/images/module.jpg';
import seriesSelectImage from '../../../assets/images/series_select.png';
import siteConditionsImage from '../../../assets/images/site_conditions.png';
import windExposureImage from '../../../assets/images/wind_exposure.png';
import quantityImage from '../../../assets/images/quantity.png';

const STEP_GRAPHICS = {
    product: seriesSelectImage,
    site_conditions: siteConditionsImage,
    frame_duty_and_wind: windExposureImage,
    pole_mount_quantity: quantityImage,
};

const STEP_GRAPHIC_ALT = {
    product: 'Beam series selection',
    site_conditions: 'Ground clearance and winter tilt',
    frame_duty_and_wind: 'Frame duty and wind exposure',
    pole_mount_quantity: 'Pole mount quantity',
};

const NUMBER_ROWS_STANDARD = [3, 4, 5];
const DESIGN_INTAKE_STEP_KEYS = [
    'product',
    'site_conditions',
    'module_layout',
    'module_dimensions',
    'frame_duty_and_wind',
    'pole_mount_quantity',
];
const MODULE_PREVIEW_MAX = 20;
const DEFAULT_POLE_MOUNT_QUANTITY = 1;

const FIELD_TOOLTIPS = {
    ground_clearance: 'This measurement provides distance between the ground and your modules.',
    tilt_angle: 'The maximum tilt angle of your array',
    number_columns: 'Number of modules vertically',
    number_rows: 'Number of modules horizontally',
    module_width: 'Short dimension of module converted to inches',
    module_length: 'Long dimension of module converted to inches',
    exposure_category: 'The enviroment in which your array will be installed.',
    pole_mount_quantity: 'How many mounts of this design will you need?',
    design_name: 'A label to identify this design in your project.',
};

const FieldLabelWithTooltip = ({ htmlFor, label, tooltip }) => (
    <div className="form-label-row">
        <label htmlFor={htmlFor} className="form-label">
            {label}
        </label>
        <span className="field-info-tooltip-wrap">
            <button type="button" className="field-info-icon-btn" aria-label={`About ${label}`}>
                <FontAwesomeIcon icon={faInfoCircle} aria-hidden />
            </button>
            <span className="field-info-tooltip" role="tooltip">
                {tooltip}
            </span>
        </span>
    </div>
);

const parseLayoutCount = (value, fallback) => {
    const trimmed = String(value).trim();
    if (trimmed === '') return fallback;
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n < 1) return fallback;
    return Math.min(n, MODULE_PREVIEW_MAX);
};

const MODULE_PORTRAIT_WIDTH_OVER_HEIGHT = 0.5;
const MODULE_CELL_PADDING_PX = 4;
const MODULE_CELL_MARGIN_PX = 4;
const MODULE_DIMENSION_PREVIEW_MAX_HEIGHT_PX = 507;
const DEFAULT_MODULE_WIDTH_IN = 40;
const DEFAULT_MODULE_LENGTH_IN = 65;

const parseDimensionInches = (value, fallback) => {
    const trimmed = String(value).trim();
    if (trimmed === '') return fallback;
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return n;
};

const formatDimensionLabel = (value, fallback) => {
    const trimmed = String(value).trim();
    if (trimmed === '') return String(fallback);
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n) || n <= 0) return String(fallback);
    return trimmed;
};

const ModuleDimensionsPreview = ({ width, length }) => {
    const moduleWidthIn = parseDimensionInches(width, DEFAULT_MODULE_WIDTH_IN);
    const moduleLengthIn = parseDimensionInches(length, DEFAULT_MODULE_LENGTH_IN);
    const widthLabel = formatDimensionLabel(width, DEFAULT_MODULE_WIDTH_IN);
    const lengthLabel = formatDimensionLabel(length, DEFAULT_MODULE_LENGTH_IN);
    const previewHeightPx = MODULE_DIMENSION_PREVIEW_MAX_HEIGHT_PX;
    const previewWidthPx = Math.max(
        48,
        Math.round(previewHeightPx * (moduleWidthIn / moduleLengthIn))
    );
    const panelWidthPx = previewHeightPx;
    const panelHeightPx = previewWidthPx;

    return (
        <div className="module-dimensions-preview" aria-label={`Module ${widthLabel} by ${lengthLabel} inches`}>
            <div className="module-dimensions-layout">
                <div className="module-dimensions-stack">
                    <div
                        className="module-dim-panel"
                        style={{
                            width: `${panelWidthPx}px`,
                            height: `${panelHeightPx}px`,
                        }}
                    >
                        <img
                            src={moduleImage}
                            alt=""
                            className="module-dim-image"
                            style={{
                                width: `${previewWidthPx}px`,
                                height: `${previewHeightPx}px`,
                            }}
                        />
                    </div>
                    <div className="module-dim-width-axis" style={{ width: `${panelWidthPx}px` }}>
                        <span className="dim-line dim-line-h" aria-hidden="true" />
                        <span className="module-dim-label">{lengthLabel} in</span>
                    </div>
                </div>
                <div className="module-dim-length-axis" style={{ height: `${panelHeightPx}px` }}>
                    <span className="dim-line dim-line-v" aria-hidden="true" />
                    <span className="module-dim-label">{widthLabel} in</span>
                </div>
            </div>
        </div>
    );
};

const MODULE_ARRAY_PREVIEW_MAX_HEIGHT_PX = 300;
const MODULE_ARRAY_MIN_CELL_VISUAL_HEIGHT_PX = 16;

const ModuleArrayPreview = ({ columns, rows }) => {
    const containerRef = useRef(null);
    const [containerWidthPx, setContainerWidthPx] = useState(0);

    const columnCount = parseLayoutCount(columns, 3);
    const rowCount = parseLayoutCount(rows, 4);
    const cellCount = columnCount * rowCount;
    const cellSpacingPx = MODULE_CELL_PADDING_PX + MODULE_CELL_MARGIN_PX;

    const idealCellVisualHeightPx = Math.max(
        MODULE_ARRAY_MIN_CELL_VISUAL_HEIGHT_PX,
        Math.floor(MODULE_ARRAY_PREVIEW_MAX_HEIGHT_PX / rowCount)
    );
    const idealCellVisualWidthPx = Math.round(idealCellVisualHeightPx / MODULE_PORTRAIT_WIDTH_OVER_HEIGHT);
    const idealCellOuterWidthPx = idealCellVisualWidthPx + cellSpacingPx * 2;
    const idealGridWidthPx = columnCount * idealCellOuterWidthPx;

    const widthScale =
        containerWidthPx > 0 && idealGridWidthPx > containerWidthPx
            ? containerWidthPx / idealGridWidthPx
            : 1;

    const cellVisualHeightPx = Math.max(
        MODULE_ARRAY_MIN_CELL_VISUAL_HEIGHT_PX,
        Math.floor(idealCellVisualHeightPx * widthScale)
    );
    const cellVisualWidthPx = Math.round(cellVisualHeightPx / MODULE_PORTRAIT_WIDTH_OVER_HEIGHT);
    const cellOuterWidthPx = cellVisualWidthPx + cellSpacingPx * 2;
    const cellOuterHeightPx = cellVisualHeightPx + cellSpacingPx * 2;

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        const updateWidth = () => {
            setContainerWidthPx(el.clientWidth);
        };

        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={containerRef}
            className="module-array-preview"
            aria-label={`${columnCount} columns by ${rowCount} rows`}
        >
            <div
                className="module-array-grid"
                style={{
                    gridTemplateColumns: `repeat(${columnCount}, ${cellOuterWidthPx}px)`,
                    gridTemplateRows: `repeat(${rowCount}, ${cellOuterHeightPx}px)`,
                    '--module-cell-visual-width': `${cellVisualWidthPx}px`,
                    '--module-cell-visual-height': `${cellVisualHeightPx}px`,
                }}
            >
                {Array.from({ length: cellCount }, (_, index) => (
                    <div key={index} className="module-array-cell-wrap">
                        <img src={moduleImage} alt="" className="module-array-cell" />
                    </div>
                ))}
            </div>
            <p className="module-array-caption">
                {columnCount} × {rowCount} module array
            </p>
        </div>
    );
};

export const DesignIntakeFormBody = ({
    project,
    design,
    onClose,
    setDesigns,
    initialDesignName = '',
    embedded = false,
    controlledStepIndex,
    onControlledStepIndexChange,
    onBackFromFirstStep,
    stepDisplayNumber,
    totalStepCount,
    onCustomSubmit,
    submitButtonTextOverride,
}) => {
    const { user, token, base_url } = useAppContext();
    const [internalStepIndex, setInternalStepIndex] = useState(0);
    const isControlled = controlledStepIndex !== undefined && onControlledStepIndexChange !== undefined;
    const stepIndex = isControlled ? controlledStepIndex : internalStepIndex;

    const goToStep = (nextIndex) => {
        const clamped = Math.max(0, Math.min(DESIGN_INTAKE_STEP_KEYS.length - 1, nextIndex));
        if (isControlled) {
            onControlledStepIndexChange(clamped);
        } else {
            setInternalStepIndex(clamped);
        }
    };
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState(() => ({
        product_type: PRODUCT_STANDARD,
        number_columns: design?.sky_civ_details?.file_data?.number_columns || 3,
        number_rows: design?.sky_civ_details?.file_data?.number_rows || 4,
        tilt_angle: design?.sky_civ_details?.file_data?.tilt_angle || 50,
        pole_spacing_override: design?.sky_civ_details?.file_data?.pole_override || 'auto',
        module_width: design?.sky_civ_details?.file_data?.module_width || 40,
        module_length: design?.sky_civ_details?.file_data?.module_length || 65,
        ground_clearance: design?.sky_civ_details?.file_data?.ground_clearance || 5,
        exposure_category: design?.sky_civ_details?.file_data?.exposure_category || 'C',
        pole_mount_quantity: design?.quantity || DEFAULT_POLE_MOUNT_QUANTITY,
        design_name: initialDesignName || '',
    }));

    const setField = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const numberRowOptions = NUMBER_ROWS_STANDARD;

    useEffect(() => {
        const n = parseInt(formData.number_rows, 10);
        if (numberRowOptions.includes(n)) return;
        setFormData((prev) => ({ ...prev, number_rows: 4 }));
    }, [numberRowOptions, formData.number_rows]);

    const stepKeys = DESIGN_INTAKE_STEP_KEYS;

    useEffect(() => {
        if (stepIndex > stepKeys.length - 1) {
            goToStep(stepKeys.length - 1);
        }
    }, [stepIndex, stepKeys.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const currentStepKey = stepKeys[stepIndex] || 'product';
    const stepGraphicSrc = STEP_GRAPHICS[currentStepKey];

    const renderStepBody = () => {
        if (currentStepKey === 'product') {
            return (
                <>
                    <div className="form-group">
                        <label className="form-label">Beam series</label>
                        <div className="project-type-options">
                            <div className="project-type-card selected project-type-card--locked">
                                <div className="project-type-content">
                                    <div className="project-type-header">
                                        <FontAwesomeIcon icon={faIndustry} />
                                        <span>Standard Beam Series</span>
                                    </div>
                                    <p>{PRODUCT_DESCRIPTIONS[PRODUCT_STANDARD]}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        if (currentStepKey === 'site_conditions') {
            return (
                <div className="intake-grid">
                    <div className="form-group compact">
                        <FieldLabelWithTooltip
                            htmlFor="ground_clearance"
                            label="Ground clearance"
                            tooltip={FIELD_TOOLTIPS.ground_clearance}
                        />
                        <input
                            id="ground_clearance"
                            className="form-input"
                            value={formData.ground_clearance}
                            onChange={(e) => setField('ground_clearance', e.target.value)}
                        />
                    </div>
                    <div className="form-group compact">
                        <FieldLabelWithTooltip
                            htmlFor="tilt_angle"
                            label="Winter Tilt Angle"
                            tooltip={FIELD_TOOLTIPS.tilt_angle}
                        />
                        <input
                            id="tilt_angle"
                            className="form-input"
                            value={formData.tilt_angle}
                            onChange={(e) => setField('tilt_angle', e.target.value)}
                        />
                    </div>
                </div>
            );
        }

        if (currentStepKey === 'module_layout') {
            return (
                <div className="intake-grid">
                    <div className="form-group compact">
                        <FieldLabelWithTooltip
                            htmlFor="number_rows"
                            label="Module rows"
                            tooltip={FIELD_TOOLTIPS.number_rows}
                        />
                        <select
                            id="number_rows"
                            className="form-select"
                            value={formData.number_rows}
                            onChange={(e) => setField('number_rows', e.target.value)}
                        >
                            {numberRowOptions.map((r) => (
                                <option key={r} value={r}>
                                    {r}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group compact">
                        <FieldLabelWithTooltip
                            htmlFor="number_columns"
                            label="Module columns"
                            tooltip={FIELD_TOOLTIPS.number_columns}
                        />
                        <input
                            id="number_columns"
                            className="form-input"
                            value={formData.number_columns}
                            onChange={(e) => setField('number_columns', e.target.value)}
                        />
                    </div>
                </div>
            );
        }

        if (currentStepKey === 'module_dimensions') {
            return (
                <div className="intake-grid">
                    <div className="form-group compact">
                        <FieldLabelWithTooltip
                            htmlFor="module_length"
                            label="Long Dimension"
                            tooltip={FIELD_TOOLTIPS.module_length}
                        />
                        <input
                            id="module_length"
                            className="form-input"
                            value={formData.module_length}
                            onChange={(e) => setField('module_length', e.target.value)}
                        />
                    </div>
                    <div className="form-group compact">
                        <FieldLabelWithTooltip
                            htmlFor="module_width"
                            label="Short Dimension"
                            tooltip={FIELD_TOOLTIPS.module_width}
                        />
                        <input
                            id="module_width"
                            className="form-input"
                            value={formData.module_width}
                            onChange={(e) => setField('module_width', e.target.value)}
                        />
                    </div>
                </div>
            );
        }

        if (currentStepKey === 'frame_duty_and_wind') {
            return (
                <div className="form-group wind-exposure-step">
                    <FieldLabelWithTooltip
                        htmlFor="exposure_category"
                        label="Wind exposure category"
                        tooltip={FIELD_TOOLTIPS.exposure_category}
                    />
                    <select
                        id="exposure_category"
                        className="form-select wind-exposure-select"
                        value={formData.exposure_category}
                        onChange={(e) => setField('exposure_category', e.target.value)}
                    >
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                    </select>
                </div>
            );
        }

        return (
            <>
                <div className="intake-grid final-step-fields">
                    <div className="form-group compact design-name-step">
                        <FieldLabelWithTooltip
                            htmlFor="design_name"
                            label="Design name"
                            tooltip={FIELD_TOOLTIPS.design_name}
                        />
                        <p className="intake-step-hint">
                            Choose a name you will recognize when reviewing designs for this project.
                        </p>
                        <input
                            id="design_name"
                            type="text"
                            className="form-input design-name-input"
                            value={formData.design_name}
                            onChange={(e) => setField('design_name', e.target.value)}
                            placeholder="e.g. South array — 4×5 modules"
                            autoComplete="off"
                        />
                    </div>
                    <div className="form-group compact pole-mount-quantity-step">
                        <FieldLabelWithTooltip
                            htmlFor="pole_mount_quantity"
                            label="How many pole mounts do you plan to use for this project?"
                            tooltip={FIELD_TOOLTIPS.pole_mount_quantity}
                        />
                        <p className="intake-step-hint">
                            Enter the total number of pole mount systems you expect to install at this site.
                        </p>
                        <div className="quantity-pill-selector">
                            <input
                                id="pole_mount_quantity"
                                type="number"
                                className="pill-qty-input"
                                value={formData.pole_mount_quantity}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    setField(
                                        'pole_mount_quantity',
                                        Number.isFinite(value) && value > 0 ? value : DEFAULT_POLE_MOUNT_QUANTITY
                                    );
                                }}
                                min="1"
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const openAutoDesignerWithIntake = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const intakeFormData = { ...formData, product_type: PRODUCT_STANDARD };
            if (onCustomSubmit) {
                await onCustomSubmit({ formData: intakeFormData, runAutoDesigner: true, isEcoBeam: false });
            } else {
                await createDesignFromIntake({
                    token,
                    base_url,
                    project,
                    formData: intakeFormData,
                    runAutoDesigner: true,
                    userEmail: user?.email,
                    setDesigns,
                    deferAutoDesignerOpen: true,
                });
                onClose();
            }
        } catch (error) {
            console.error('Error creating design:', error);
            if (window.showAlert) {
                window.showAlert(500, error?.message || 'Failed to create design. Please try again later.');
            }
        }
    };

    const isLastStep = stepIndex === stepKeys.length - 1;
    const submitButtonText = submitButtonTextOverride ?? (design ? 'Iterate Design' : 'Open Auto-Designer');

    const displayedStepNumber = stepDisplayNumber ?? stepIndex + 1;
    const displayedTotalSteps = totalStepCount ?? stepKeys.length;

    const handleBack = () => {
        if (stepIndex === 0) {
            if (onBackFromFirstStep) {
                onBackFromFirstStep();
            } else {
                onClose();
            }
            return;
        }
        goToStep(stepIndex - 1);
    };

    const form = (
        <div className="design-intake-form">
            <div className="design-intake-scroll-area">
                <p className="design-intake-step">
                    Step {displayedStepNumber} of {displayedTotalSteps}
                </p>

                <div
                    className={`step-graphic${
                        currentStepKey === 'site_conditions' || currentStepKey === 'frame_duty_and_wind'
                            ? ' step-graphic--enlarged'
                            : ''
                    }`}
                >
                    {currentStepKey === 'module_layout' ? (
                        <ModuleArrayPreview columns={formData.number_columns} rows={formData.number_rows} />
                    ) : currentStepKey === 'module_dimensions' ? (
                        <ModuleDimensionsPreview width={formData.module_width} length={formData.module_length} />
                    ) : stepGraphicSrc ? (
                        <img alt={STEP_GRAPHIC_ALT[currentStepKey] || 'Step graphic'} src={stepGraphicSrc} />
                    ) : null}
                </div>

                {renderStepBody()}

                {isLastStep && (
                    <div className="design-intake-submit-notice" role="note">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="design-intake-submit-notice-icon" aria-hidden />
                        <p>
                            Submitting this form will open the Auto-Designer. Press the{' '}
                            <strong>green button</strong> there to finalize your design. Do not close the window until
                            prompted. Design generation can take up to 3 minutes.
                        </p>
                    </div>
                )}
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    onClick={handleBack}
                    className="btn btn-secondary"
                >
                    {stepIndex === 0 && !onBackFromFirstStep ? 'Cancel' : 'Back'}
                </button>
                {isLastStep ? (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                            openAutoDesignerWithIntake().finally(() => setIsSubmitting(false));
                        }}
                        disabled={isSubmitting || !String(formData.design_name).trim()}
                    >
                        <FontAwesomeIcon icon={faHammer} /> {submitButtonText}
                    </button>
                ) : (
                    <button type="button" className="btn btn-primary" onClick={() => goToStep(stepIndex + 1)}>
                        Next
                    </button>
                )}
            </div>
        </div>
    );

    if (embedded) {
        return form;
    }

    return <Modal form={form} setIsModalOpen={onClose} isWide />;
};

export const DesignIntakeForm = (props) => <DesignIntakeFormBody {...props} />;
